import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Grid,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Autocomplete,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Group as TeamIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../utils/apiClient';

const TeamAssignment = ({ workflowData, onDataUpdate, onNext, onBack, isValid }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Local state
  const [assignmentMode, setAssignmentMode] = useState('inherit'); // 'inherit', 'individual', 'none'
  const [availableTeams, setAvailableTeams] = useState([]);
  const [parentTeams, setParentTeams] = useState([]);
  const [splitTeamAssignments, setSplitTeamAssignments] = useState({});
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);

  // Load teams data
  useEffect(() => {
    loadTeamsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowData.parentNcessch]);

  // Update workflow data when team assignments change
  useEffect(() => {
    onDataUpdate({
      teamAssignments: {
        mode: assignmentMode,
        assignments: splitTeamAssignments,
        parentTeams: parentTeams
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentMode, splitTeamAssignments, parentTeams]);

  const loadTeamsData = async () => {
    if (!workflowData.parentNcessch) return;

    setIsLoadingTeams(true);
    try {
      // Load parent school teams
      const parentTeamsResponse = await apiClient.get(`/v2/splits/${workflowData.parentNcessch}/teams`);
      if (parentTeamsResponse.parent_teams) {
        setParentTeams(parentTeamsResponse.parent_teams);
      }

      // Load all available teams for individual assignment
      const allTeamsResponse = await apiClient.get('/v2/admin/teams?getAllTeams=true');
      if (allTeamsResponse.teams) {
        setAvailableTeams(allTeamsResponse.teams);
      }

      enqueueSnackbar('Team data loaded successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error loading teams data:', error);
      enqueueSnackbar('Error loading team data', { variant: 'error' });
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Handle assignment mode change
  const handleAssignmentModeChange = (event) => {
    const newMode = event.target.value;
    setAssignmentMode(newMode);

    // Clear individual assignments when switching modes
    if (newMode !== 'individual') {
      setSplitTeamAssignments({});
    }
  };

  // Handle individual team assignment
  const handleIndividualTeamAssignment = (splitIndex, team) => {
    setSplitTeamAssignments(prev => ({
      ...prev,
      [splitIndex]: team
    }));
  };

  // Remove individual team assignment
  const removeIndividualTeamAssignment = (splitIndex) => {
    setSplitTeamAssignments(prev => {
      const updated = { ...prev };
      delete updated[splitIndex];
      return updated;
    });
  };

  // Get team assignment summary for a split
  const getTeamAssignmentSummary = (splitIndex) => {
    // const split = workflowData.splits[splitIndex];
    
    switch (assignmentMode) {
      case 'inherit':
        return parentTeams.length > 0 
          ? `Will inherit ${parentTeams.length} team(s) from parent`
          : 'No parent teams to inherit';
      
      case 'individual':
        return splitTeamAssignments[splitIndex] 
          ? `Assigned to ${splitTeamAssignments[splitIndex].name}`
          : 'No team assigned';
      
      case 'none':
        return 'No team assignment (can be assigned later)';
      
      default:
        return 'Unknown assignment mode';
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TeamIcon color="primary" />
        Team Assignment Configuration
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure how split schools will be assigned to teams. You can inherit parent school teams,
        assign individual teams, or skip team assignment for now.
      </Typography>

      {/* Parent School Team Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Parent School Team Assignments
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {workflowData.parentSchool?.name} ({workflowData.parentNcessch})
          </Typography>
          
          {isLoadingTeams ? (
            <Typography variant="body2">Loading team information...</Typography>
          ) : parentTeams.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Current team assignments:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {parentTeams.map((team, index) => (
                  <Chip 
                    key={index}
                    label={team.team_name}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Alert severity="info">
              <InfoIcon />
              This parent school is not currently assigned to any teams.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Assignment Mode Selection */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Team Assignment Mode
          </Typography>
          
          <FormControl component="fieldset">
            <RadioGroup
              value={assignmentMode}
              onChange={handleAssignmentModeChange}
            >
              <FormControlLabel
                value="inherit"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">
                      Inherit Parent Teams
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All split schools will be assigned to the same teams as the parent school
                      {parentTeams.length > 0 && ` (${parentTeams.length} team${parentTeams.length !== 1 ? 's' : ''})`}
                    </Typography>
                  </Box>
                }
                disabled={parentTeams.length === 0}
              />
              
              <FormControlLabel
                value="individual"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">
                      Individual Assignment
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Assign each split school to different teams individually
                    </Typography>
                  </Box>
                }
              />
              
              <FormControlLabel
                value="none"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">
                      No Assignment (Skip for Now)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Skip team assignment during split processing. Teams can be assigned later.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      {/* Individual Team Assignment Interface */}
      {assignmentMode === 'individual' && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Individual Team Assignments
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Select a team for each split school. Teams are optional - you can leave some unassigned.
            </Typography>

            {workflowData.splits?.map((split, splitIndex) => (
              <Accordion key={split.id || splitIndex} sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Typography variant="subtitle1">
                      {split.displayName}
                    </Typography>
                    <Chip 
                      label={`Grades: ${split.gradesServed?.join(', ')}`} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    {splitTeamAssignments[splitIndex] && (
                      <Chip
                        label={splitTeamAssignments[splitIndex].name}
                        color="success"
                        size="small"
                      />
                    )}
                  </Box>
                </AccordionSummary>
                
                <AccordionDetails>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={8}>
                      <Autocomplete
                        options={availableTeams}
                        getOptionLabel={(option) => `${option.name} (${option.description || 'No description'})`}
                        value={splitTeamAssignments[splitIndex] || null}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            handleIndividualTeamAssignment(splitIndex, newValue);
                          } else {
                            removeIndividualTeamAssignment(splitIndex);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Team"
                            placeholder="Search teams..."
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            <Box>
                              <Typography variant="body2">
                                {option.name}
                              </Typography>
                              {option.description && (
                                <Typography variant="caption" color="text.secondary">
                                  {option.description}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        )}
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      {splitTeamAssignments[splitIndex] && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => removeIndividualTeamAssignment(splitIndex)}
                          size="small"
                        >
                          Remove Assignment
                        </Button>
                      )}
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Assignment Summary */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Team Assignment Summary
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Split School</strong></TableCell>
                  <TableCell><strong>Grades</strong></TableCell>
                  <TableCell><strong>Team Assignment</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {workflowData.splits?.map((split, splitIndex) => (
                  <TableRow key={split.id || splitIndex}>
                    <TableCell>
                      <Typography variant="body2">
                        {split.displayName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        -{split.suffix}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {split.gradesServed?.map(grade => (
                          <Chip 
                            key={grade} 
                            label={grade} 
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getTeamAssignmentSummary(splitIndex)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Warnings and Information */}
      {assignmentMode === 'inherit' && parentTeams.length === 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>No Parent Teams to Inherit</AlertTitle>
          The parent school is not assigned to any teams. Consider using individual assignment 
          or skipping team assignment for now.
        </Alert>
      )}

      {assignmentMode === 'none' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Team Assignment Skipped</AlertTitle>
          Split schools will be created without team assignments. You can assign teams later 
          through the team management interface.
        </Alert>
      )}

      {/* Next Step Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={onNext}
          size="large"
        >
          Continue to Final Review
        </Button>
      </Box>
    </Box>
  );
};

export default TeamAssignment;
